import React, { useRef } from 'react';
import { Search, X } from 'lucide-react';

/**
 * THE SEARCH BOX — one, for every list in the console.
 *
 * Forty-odd lists each drew their own: a magnifier absolutely placed over an <input>, in slightly
 * different sizes, and none of them could be emptied except by selecting the text and deleting it.
 * This is that box once: the magnifier, the text, and — only while there is text — a quiet × that
 * clears it and puts the cursor back. Escape clears it too (a second Escape, with nothing to clear,
 * goes on to whatever owns Escape around it: a modal, a popover).
 *
 *   value / onChange(text)  controlled; onChange gets the STRING, not the event
 *   placeholder, className  className places and sizes the box (its width), e.g. "w-[260px]", "w-full"
 *   size                    'sm' (h-8, the filter bars) | 'md' (h-9, a page's main search)
 *   tone                    'plain' (white) | 'sunken' (#FBFAF7 until focused, inside a tinted strip)
 *   rest                    passed to the <input> (autoFocus, aria-label, onKeyDown, onFocus, …)
 *
 * No free-form input classes: Tailwind resolves two conflicting utilities by stylesheet order, not by
 * the order they are written, so "h-8 h-9" is a coin toss. The variants are named instead.
 */
const SIZES = { sm: 'h-8 text-[12px]', md: 'h-9 text-[12.5px]' };
const TONES = { plain: 'bg-white', sunken: 'bg-[#FBFAF7] focus:bg-white' };
export const SearchInput = ({ value, onChange, placeholder = 'Search…', className = 'w-[260px]', size = 'sm', tone = 'plain', inputRef, ...rest }) => {
  const own = useRef(null);
  const ref = inputRef || own;
  const text = value ?? '';
  const clear = () => { onChange(''); ref.current?.focus(); };
  return (
    <div className={`relative ${className}`}>
      <Search aria-hidden="true" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF] pointer-events-none" strokeWidth={2} />
      <input
        ref={ref}
        type="text"
        value={text}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={rest['aria-label'] || placeholder}
        {...rest}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && text) { e.preventDefault(); e.stopPropagation(); onChange(''); return; }
          rest.onKeyDown?.(e);
        }}
        className={`${SIZES[size] || SIZES.sm} ${TONES[tone] || TONES.plain} w-full border border-[#E6E4E1] rounded-md pl-8 ${text ? 'pr-7' : 'pr-3'} text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#D8D5CF]`}
      />
      {text && (
        <button type="button" onClick={clear} aria-label="Clear search" title="Clear"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center rounded text-[#9CA3AF] hover:text-[#4B5563] hover:bg-[#F2F1ED]">
          <X className="w-3 h-3" strokeWidth={2.25} />
        </button>
      )}
    </div>
  );
};
